import { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useFormWithValidation, useProjectSiteParams } from '@/shared/lib';
import {
  CONSIGNMENT_STATUS,
  useCreateConsignment,
  useUpdateConsignmentWithAttachments,
  type ConsignmentSummary
} from '@/entities/consignments';
import type { AttachmentsData } from '@/features/consignment-attachment-list';

import { createConsignmentSchema, type ConsignmentFormValues } from './validation';
import { transformResponseToForm, buildUpdateRequest, transformFormToCreateRequest } from '../lib';

export type ConsignmentFormMode = 'create' | 'edit';

interface UseConsignmentFormProps {
  /** Whether the form creates a new consignment or edits an existing one. Defaults to 'edit'. */
  mode?: ConsignmentFormMode;
  /** Required in edit mode; ignored in create mode. */
  consignmentId?: string;
  consignmentData?: ConsignmentSummary;
  /** Best-effort initial values for create mode (e.g. prefilled from a service request). */
  prefillValues?: Partial<ConsignmentFormValues>;
  onSave: (data: unknown) => void;
  getAttachments?: () => AttachmentsData | null;
}

export const useConsignmentForm = ({
  mode = 'edit',
  consignmentId,
  consignmentData,
  prefillValues,
  onSave,
  getAttachments
}: UseConsignmentFormProps) => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const isCreate = mode === 'create';

  const consignmentSchema = createConsignmentSchema(t);
  const form = useFormWithValidation(consignmentSchema, {
    status: CONSIGNMENT_STATUS.NR,
    priority: 'normal',
    plannedMode: 'none'
  });

  const handleSuccess = useCallback(
    (result: unknown) => {
      const attachments = getAttachments?.();
      const hasLocalAttachments = !!attachments?.getAttachmentFiles()?.files?.length;

      // Clear local attachments after successful upload (if any were uploaded)
      if (hasLocalAttachments) {
        attachments?.clearLocalAttachments();
      }

      if (isCreate) {
        toast.success(t('consignment.create.success', 'Consignment created successfully'));
      } else if (hasLocalAttachments) {
        toast.success(
          t('consignment.edit.saveSuccessWithAttachments', 'Consignment and attachments saved successfully')
        );
      } else {
        toast.success(t('consignment.edit.saveSuccess', 'Consignment saved successfully'));
      }

      onSave(result);
    },
    [t, onSave, getAttachments, isCreate]
  );

  const handleError = useCallback(
    (error: unknown) => {
      toast.error(t('consignment.edit.saveError', 'Failed to save consignment'));
      console.error('Failed to save consignment:', error);
    },
    [t]
  );

  const { createConsignment, isLoading: isCreating } = useCreateConsignment({
    onSuccess: handleSuccess,
    onError: handleError
  });

  const { updateConsignmentWithAttachments, isLoading: isUpdating } = useUpdateConsignmentWithAttachments({
    onSuccess: handleSuccess,
    onError: handleError
  });

  const onSubmit = useCallback(
    async (data: ConsignmentFormValues) => {
      if (!projectId || !siteId) {
        toast.error(t('consignment.edit.missingParams', 'Missing project or site parameters'));
        return;
      }

      if (isCreate) {
        const reqData = transformFormToCreateRequest(data);
        await createConsignment({ projectId, siteId }, reqData);
        return;
      }

      if (!consignmentId) {
        toast.error(t('consignment.edit.missingParams', 'Missing project or site parameters'));
        return;
      }

      const { params, updateRequest, attachmentsData } = buildUpdateRequest(
        consignmentId,
        projectId,
        siteId,
        data,
        getAttachments?.()
      );

      await updateConsignmentWithAttachments(params, updateRequest, attachmentsData);
    },
    [isCreate, consignmentId, projectId, siteId, createConsignment, updateConsignmentWithAttachments, t, getAttachments]
  );

  useEffect(() => {
    if (consignmentData) {
      const formDefaults = transformResponseToForm(consignmentData);
      form.reset(formDefaults);
    }
  }, [consignmentData, form]);

  // Apply create-mode prefill (e.g. from a service request) once, preserving form defaults.
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (isCreate && prefillValues && !prefillAppliedRef.current) {
      prefillAppliedRef.current = true;
      form.reset({ ...form.getValues(), ...prefillValues });
    }
  }, [isCreate, prefillValues, form]);

  return {
    form,
    onSubmit,
    isUpdating,
    isCreating,
    isSaving: isCreate ? isCreating : isUpdating,
    consignmentData
  };
};
